import React, { useState } from 'react'
import CopyToClipboard from 'react-copy-to-clipboard'
import PropTypes from 'prop-types'
import { connect, useStore } from 'react-redux'
import { Badge, Container, Table } from '@resurs/midas-react'
import { midas } from '@resurs/midas'
import Page from '../../../internal/Page'
import { useOnMount } from '../../../util/utility-hooks'
import { getPaymentMethodsByChannel } from '../../../store/actions/store-info-actions'
import { getApplicationLinksForStore } from '../../../store/actions/application-links-actions'
import { getText } from '../../../internal/TextLabels'
import styles from './ApplicationLinks.module.scss'
import config from '../../../../../config'
import { ChannelType } from '../../../models/channelType'
import { genericError } from '../../../store/actions/action-utils'

export const ApplicationLinks = props => {
  const [applicationLinks, setApplicationLinks] = useState([])
  const store = useStore()

  useOnMount(() => {
    props.getPaymentMethods()

    getApplicationLinksForStore(
      store.getState().auth.store.storeUID,
      store.getState().auth.token,
    )
      .then(result => {
        setApplicationLinks(result)
      })
      .catch(error => {
        if (error.status === 404) {
          setApplicationLinks([])
        } else {
          genericError(error)
          throw error
        }
      })
  })

  function getApplyUrl(urlId) {
    return `${config.applyRetailCreditAppUrl}/${urlId}`
  }

  function getPaymentMethodDescription(methodId) {
    const paymentMethod = props.paymentMethods.find(
      method => method.id === methodId,
    )

    return (
      paymentMethod?.description ?? (
        <em>{getText('applicationLinks.paymentMethod.missing')}</em>
      )
    )
  }

  return (
    <Container grid1280>
      <Container>
        <h4>{getText('applicationLinks.title')}</h4>
        <div>{getText('applicationLinks.description')}</div>
      </Container>
      <Page>
        <Table disableHover striped>
          <thead className={midas.textBold}>
            <tr>
              <td className={`${midas.textLeft} ${styles.nameColumn}`}>
                {getText('applicationLinks.headers.paymentMethod')}
              </td>
              <td>{getText('applicationLinks.headers.link')}</td>
              <td className={styles.statusColumn}>
                {getText('applicationLinks.headers.status')}
              </td>
            </tr>
          </thead>
          <tbody>
            {applicationLinks.length ? (
              applicationLinks.map(link => {
                if (link.status !== 'BROKEN') {
                  return (
                    <tr key={link.urlId}>
                      <td>
                        {getPaymentMethodDescription(link.paymentMethodId)}
                      </td>
                      <td className={styles.linkColumn}>
                        <a
                          href={getApplyUrl(link.urlId)}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {getApplyUrl(link.urlId)}
                        </a>
                        <CopyToClipboard text={getApplyUrl(link.urlId)}>
                          <i
                            role="presentation"
                            title={`Copy URL: ${getApplyUrl(link.urlId)}`}
                            className={`${styles.icon} ${midas.midasFiles4}`}
                            onClick={c => c.stopPropagation()}
                          />
                        </CopyToClipboard>
                      </td>
                      <td>
                        <Badge
                          kind="primary"
                          label={getText('applicationLinks.status.active')}
                        />
                      </td>
                    </tr>
                  )
                }
                return (
                  <tr key={link.urlId} className={styles.broken}>
                    <td>{getPaymentMethodDescription(link.paymentMethodId)}</td>
                    <td className={styles.linkColumn}>
                      <span>{getApplyUrl(link.urlId)}</span>
                      <CopyToClipboard text={getApplyUrl(link.urlId)}>
                        <i
                          role="presentation"
                          title={`Copy URL: ${getApplyUrl(link.urlId)}`}
                          className={`${styles.icon} ${midas.midasFiles4}`}
                          onClick={c => c.stopPropagation()}
                        />
                      </CopyToClipboard>
                    </td>
                    <td>
                      <Badge
                        kind="danger"
                        label={getText('applicationLinks.status.broken')}
                      />
                    </td>
                  </tr>
                )
              })
            ) : (
              <tr>
                <td
                  className={[midas.textCenter, styles.greyText].join(' ')}
                  colSpan={3}
                >
                  {getText('applicationLinks.noLinks')}
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </Page>
    </Container>
  )
}

ApplicationLinks.propTypes = {
  getPaymentMethods: PropTypes.func.isRequired,
  paymentMethods: PropTypes.array.isRequired,
}

const mapStateToProps = state => {
  return {
    currentStore: state.storeInfo.currentStore,
    paymentMethods: state.storeInfo.currentPaymentMethodsByChannel,
  }
}

const mapDispatchToProps = dispatch => ({
  getPaymentMethods: () => {
    dispatch(getPaymentMethodsByChannel(ChannelType.ONLINE_APPLICATIONS))
  },
})

export default connect(mapStateToProps, mapDispatchToProps)(ApplicationLinks)
